import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Brain, Zap, Target, TrendingUp, Eye, MessageCircle, Lock } from 'lucide-react';
export function PSIProBenefitsSection() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  return <section className="relative py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-gradient-to-b from-black via-purple-950/20 to-black overflow-hidden" data-id="element-4596">
      {/* Mystical background effects - matching PSIGatheringSection */}
      <div className="absolute inset-0 opacity-10" style={{
      backgroundImage: `radial-gradient(circle at 20% 50%, rgba(168, 85, 247, 0.3) 0%, transparent 50%),
                           radial-gradient(circle at 80% 50%, rgba(59, 130, 246, 0.3) 0%, transparent 50%)`
    }} data-id="element-4597" />

      {/* Glowing orbs */}
      <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl animate-pulse-slow" data-id="element-4598" />
      <div className="absolute bottom-1/4 right-1/3 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl animate-pulse-slow" style={{
      animationDelay: '1s'
    }} data-id="element-4599" />

      <motion.div ref={ref} initial={{
      opacity: 0,
      y: 30
    }} animate={inView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.8
    }} className="relative z-10 max-w-4xl mx-auto" data-id="element-4600">
        {/* Main headline */}
        <h2 className="text-2xl sm:text-4xl md:text-6xl font-serif text-center mb-8 sm:mb-12 leading-tight text-stone-100" style={{
        fontFamily: 'Cinzel, serif',
        textShadow: '0 0 30px rgba(168, 85, 247, 0.4)'
      }} data-id="element-4601">
          <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-500 bg-clip-text text-transparent" style={{
          textShadow: '0 0 40px rgba(168, 85, 247, 0.6)'
        }} data-id="element-4602">
            PSI PRO
          </span>{' '}
          Is Where The Paradigm Shift Happens
        </h2>

        <p className="text-lg sm:text-xl text-stone-300 text-center mb-8 sm:mb-12 md:mb-16" data-id="element-4603">
          Here's what you're actually getting when you join:
        </p>

        {/* Benefits List */}
        <div className="space-y-16" data-id="element-4604">
          {/* Benefit 1: Paradigm Shift */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="relative" data-id="element-4605">
            <div className="absolute inset-0 bg-gradient-radial from-purple-600/10 via-transparent to-transparent blur-2xl" data-id="element-4606" />

            <div className="relative bg-gradient-to-br from-purple-950/40 to-black border-2 border-purple-500/50 p-4 sm:p-6 md:p-10 rounded-lg" data-id="element-4607">
              <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6" data-id="element-4608">
                <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-purple-500 rounded-full flex items-center justify-center" data-id="element-4609">
                  <Brain className="w-5 h-5 sm:w-6 sm:h-6 text-black" data-id="element-4610" />
                </div>
                <div className="flex-1" data-id="element-4611">
                  <div className="flex items-center gap-2 mb-3" data-id="element-4612">
                    <span className="text-green-400 text-xl sm:text-2xl" data-id="element-4613">
                      ✓
                    </span>
                    <h3 className="text-lg sm:text-2xl md:text-3xl font-bold text-purple-400" data-id="element-4614">
                      THE MOST IMPORTANT PARADIGM SHIFT YOU'LL EVER MAKE ABOUT
                      PEPTIDES
                    </h3>
                  </div>
                </div>
              </div>

              <div className="space-y-4 text-base sm:text-lg text-stone-300 pl-0 sm:pl-16" data-id="element-4615">
                <p className="text-stone-100 font-semibold" data-id="element-4616">
                  This is the shift that changes everything.
                </p>

                <div className="space-y-2 pl-6 border-l-2 border-purple-500/30" data-id="element-4617">
                  <p data-id="element-4618">
                    The shift that allows you to see what's ACTUALLY possible.
                  </p>
                  <p data-id="element-4619">
                    The shift that separates the masters from everyone else.
                  </p>
                </div>

                <p className="text-amber-400 font-bold" data-id="element-4620">
                  We've NEVER seen this covered by anyone else online. Ever.
                </p>

                <p data-id="element-4621">
                  And it's something Brenden and Ali will reveal during the live
                  strategic briefings.
                </p>

                <p className="italic text-stone-400" data-id="element-4622">
                  I can't tell you what it is on this page.
                </p>

                <p className="text-stone-400" data-id="element-4623">
                  (Because frankly, if I did, you'd think I was making it up.)
                </p>

                <div className="bg-purple-950/30 border-l-4 border-purple-500 p-6 my-6" data-id="element-4624">
                  <p className="text-xl text-stone-100 font-bold mb-3" data-id="element-4625">
                    But once you see it...
                  </p>
                  <p className="text-xl text-stone-100 font-bold mb-3" data-id="element-4626">
                    Once you UNDERSTAND it...
                  </p>
                  <p className="text-xl text-purple-400 font-bold" data-id="element-4627">
                    Everything else clicks into place.
                  </p>
                </div>

                <div className="space-y-2" data-id="element-4628">
                  <p data-id="element-4629">Your entire approach to biology changes.</p>
                  <p className="text-stone-100 font-semibold" data-id="element-4630">
                    And you'll wonder how you ever operated without knowing
                    this.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Benefit 2: The Impossible */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="relative" data-id="element-4631">
            <div className="absolute inset-0 bg-gradient-radial from-cyan-600/10 via-transparent to-transparent blur-2xl" data-id="element-4632" />

            <div className="relative bg-gradient-to-br from-cyan-950/40 to-black border-2 border-cyan-500/50 p-4 sm:p-6 md:p-10 rounded-lg" data-id="element-4633">
              <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6" data-id="element-4634">
                <div className="flex-shrink-0 w-12 h-12 bg-cyan-500 rounded-full flex items-center justify-center" data-id="element-4635">
                  <Zap className="w-6 h-6 text-black" data-id="element-4636" />
                </div>
                <div className="flex-1" data-id="element-4637">
                  <div className="flex items-center gap-2 mb-3" data-id="element-4638">
                    <span className="text-green-400 text-2xl" data-id="element-4639">✓</span>
                    <h3 className="text-2xl md:text-3xl font-bold text-cyan-400" data-id="element-4640">
                      HOW TO DO THE "IMPOSSIBLE" WITH HUMAN BIOLOGY
                    </h3>
                  </div>
                </div>
              </div>

              <div className="space-y-4 text-base sm:text-lg text-stone-300 pl-0 sm:pl-16" data-id="element-4641">
                <p data-id="element-4642">
                  This is where Brenden and Ali show you what's REALLY possible
                  when you understand peptides at the highest level.
                </p>

                <div className="space-y-2 pl-6 border-l-2 border-cyan-500/30" data-id="element-4643">
                  <p data-id="element-4644">Not just "optimization."</p>
                  <p data-id="element-4645">Not just "improvement."</p>
                </div>

                <p className="text-xl text-cyan-400 font-bold" data-id="element-4646">
                  But actual REGENERATION of tissues that mainstream medicine
                  considers "impossible to heal."
                </p>

                <div className="bg-cyan-950/30 p-6 rounded-lg space-y-3" data-id="element-4647">
                  <p className="text-stone-100" data-id="element-4648">
                    <span className="text-cyan-400 font-bold" data-id="element-4649">REVERSAL</span> of
                    conditions that doctors say are "permanent."
                  </p>
                  <p className="text-stone-100" data-id="element-4650">
                    <span className="text-cyan-400 font-bold" data-id="element-4651">RESTORATION</span>{' '}
                    of function that specialists say "can't be done."
                  </p>
                </div>

                <div className="space-y-2" data-id="element-4652">
                  <p className="text-stone-100 font-semibold" data-id="element-4653">
                    You'll see real case studies.
                  </p>
                  <p className="text-stone-100 font-semibold" data-id="element-4654">
                    Real transformations.
                  </p>
                  <p className="text-stone-100 font-semibold" data-id="element-4655">
                    Real "impossible" results.
                  </p>
                </div>

                <p className="text-xl text-cyan-400 font-bold" data-id="element-4656">
                  And you'll learn the EXACT frameworks they used to create
                  them.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Benefit 3: Precision Cell™ Pro */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="relative" data-id="element-4657">
            <div className="absolute inset-0 bg-gradient-radial from-emerald-600/10 via-transparent to-transparent blur-2xl" data-id="element-4658" />

            <div className="relative bg-gradient-to-br from-emerald-950/40 to-black border-2 border-emerald-500/50 p-4 sm:p-6 md:p-10 rounded-lg" data-id="element-4659">
              <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6" data-id="element-4660">
                <div className="flex-shrink-0 w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center" data-id="element-4661">
                  <Target className="w-6 h-6 text-black" data-id="element-4662" />
                </div>
                <div className="flex-1" data-id="element-4663">
                  <div className="flex items-center gap-2 mb-3" data-id="element-4664">
                    <span className="text-green-400 text-2xl" data-id="element-4665">✓</span>
                    <h3 className="text-2xl md:text-3xl font-bold text-emerald-400" data-id="element-4666">
                      THE PRECISION CELL™ PRO METHODOLOGY
                    </h3>
                  </div>
                </div>
              </div>

              <div className="space-y-4 text-base sm:text-lg text-stone-300 pl-0 sm:pl-16" data-id="element-4667">
                <p data-id="element-4668">
                  This is the same diagnostic and protocol design framework
                  Brenden and Ali use with private clients paying $15K−$120K.
                </p>

                <p className="text-emerald-400 font-bold" data-id="element-4669">
                  You're getting access to it as part of your PSI PRO
                  membership.
                </p>

                <p className="text-stone-100 font-semibold" data-id="element-4670">
                  Which means you'll learn:
                </p>

                <div className="space-y-3 pl-6 border-l-2 border-emerald-500/30" data-id="element-4671">
                  <p className="flex items-start gap-3" data-id="element-4672">
                    <span className="text-emerald-400 flex-shrink-0" data-id="element-4673">→</span>
                    <span data-id="element-4674">
                      How to think diagnostically instead of symptomatically
                    </span>
                  </p>
                  <p className="flex items-start gap-3" data-id="element-4675">
                    <span className="text-emerald-400 flex-shrink-0" data-id="element-4676">→</span>
                    <span data-id="element-4677">
                      How to identify root causes instead of just treating
                      surface issues
                    </span>
                  </p>
                  <p className="flex items-start gap-3" data-id="element-4678">
                    <span className="text-emerald-400 flex-shrink-0" data-id="element-4679">→</span>
                    <span data-id="element-4680">
                      How to design protocols based on molecular mechanisms
                    </span>
                  </p>
                  <p className="flex items-start gap-3" data-id="element-4681">
                    <span className="text-emerald-400 flex-shrink-0" data-id="element-4682">→</span>
                    <span data-id="element-4683">How to stack peptides for synergistic effects</span>
                  </p>
                  <p className="flex items-start gap-3" data-id="element-4684">
                    <span className="text-emerald-400 flex-shrink-0" data-id="element-4685">→</span>
                    <span data-id="element-4686">
                      How to optimize protocols based on real-time feedback
                    </span>
                  </p>
                  <p className="flex items-start gap-3" data-id="element-4687">
                    <span className="text-emerald-400 flex-shrink-0" data-id="element-4688">→</span>
                    <span data-id="element-4689">
                      How to create transformations for yourself and others
                    </span>
                  </p>
                </div>

                <p className="text-xl text-emerald-400 font-bold" data-id="element-4690">
                  This is what allows you to operate at a level most
                  practitioners will never reach.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Benefit 4: State of the Union */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="relative" data-id="element-4691">
            <div className="absolute inset-0 bg-gradient-radial from-blue-600/10 via-transparent to-transparent blur-2xl" data-id="element-4692" />

            <div className="relative bg-gradient-to-br from-blue-950/40 to-black border-2 border-blue-500/50 p-4 sm:p-6 md:p-10 rounded-lg" data-id="element-4693">
              <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6" data-id="element-4694">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center" data-id="element-4695">
                  <TrendingUp className="w-6 h-6 text-black" data-id="element-4696" />
                </div>
                <div className="flex-1" data-id="element-4697">
                  <div className="flex items-center gap-2 mb-3" data-id="element-4698">
                    <span className="text-green-400 text-2xl" data-id="element-4699">✓</span>
                    <h3 className="text-2xl md:text-3xl font-bold text-blue-400" data-id="element-4700">
                      THE STATE OF THE UNION: WHAT'S REALLY HAPPENING IN 2026
                    </h3>
                  </div>
                </div>
              </div>

              <div className="space-y-4 text-base sm:text-lg text-stone-300 pl-0 sm:pl-16" data-id="element-4701">
                <p data-id="element-4702">
                  Every month, Brenden and Ali will give you a STRATEGIC
                  BRIEFING about peptides, biohacking, healing or performance
                  enhancement.
                </p>

                <div className="space-y-3 pl-6 border-l-2 border-blue-500/30" data-id="element-4703">
                  <p data-id="element-4704">What's working in the real world.</p>
                  <p data-id="element-4705">What new research has emerged.</p>
                  <p data-id="element-4706">What misinformation is being spread.</p>
                  <p data-id="element-4707">Which vendors are legitimate and which are bunk.</p>
                  <p data-id="element-4708">What trends to pay attention to and what to ignore.</p>
                </div>

                <div className="bg-blue-950/30 p-6 rounded-lg space-y-3" data-id="element-4709">
                  <p className="text-xl text-blue-400 font-bold" data-id="element-4710">
                    This is your GROUNDED GUIDANCE.
                  </p>
                  <p className="text-stone-100" data-id="element-4711">
                    Your compass in a sea of noise.
                  </p>
                  <p className="text-stone-100" data-id="element-4712">
                    Your protection against the frauds, the liars, and the
                    grifters.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Benefit 5: Elite Secrets */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="relative" data-id="element-4713">
            <div className="absolute inset-0 bg-gradient-radial from-amber-600/10 via-transparent to-transparent blur-2xl" data-id="element-4714" />

            <div className="relative bg-gradient-to-br from-amber-950/40 to-black border-2 border-amber-500/50 p-4 sm:p-6 md:p-10 rounded-lg" data-id="element-4715">
              <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6" data-id="element-4716">
                <div className="flex-shrink-0 w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center" data-id="element-4717">
                  <Lock className="w-6 h-6 text-black" data-id="element-4718" />
                </div>
                <div className="flex-1" data-id="element-4719">
                  <div className="flex items-center gap-2 mb-3" data-id="element-4720">
                    <span className="text-green-400 text-2xl" data-id="element-4721">✓</span>
                    <h3 className="text-2xl md:text-3xl font-bold text-amber-400" data-id="element-4722">
                      ELITE, HIGH-LEVEL SECRETS YOU WON'T FIND ANYWHERE ELSE
                    </h3>
                  </div>
                </div>
              </div>

              <div className="space-y-4 text-base sm:text-lg text-stone-300 pl-0 sm:pl-16" data-id="element-4723">
                <p data-id="element-4724">
                  There are things Brenden and Ali will share during these
                  sessions that you will NEVER find outside of this membership.
                </p>

                <div className="space-y-3 pl-6 border-l-2 border-amber-500/30" data-id="element-4725">
                  <p data-id="element-4726">
                    Insights that would fundamentally change how you think about
                    biology.
                  </p>
                  <p data-id="element-4727">
                    Secrets that sound too good to be true... until you see the
                    science behind them.
                  </p>
                  <p data-id="element-4728">
                    Strategies that are so effective they border on unfair
                    advantages.
                  </p>
                </div>

                <p className="italic text-stone-400" data-id="element-4729">
                  I can't reveal what these are on this page.
                </p>

                <div className="bg-amber-950/30 p-6 rounded-lg" data-id="element-4730">
                  <p className="text-xl text-stone-100 font-bold mb-3" data-id="element-4731">
                    But I can tell you this:
                  </p>
                  <div className="space-y-2" data-id="element-4732">
                    <p data-id="element-4733">
                      If you're the kind of person who values being 10-20 years
                      ahead of the mainstream...
                    </p>
                    <p data-id="element-4734">
                      If you want access to knowledge that gives you a REAL
                      edge...
                    </p>
                    <p className="text-xl text-amber-400 font-bold" data-id="element-4735">
                      Then this alone is worth the price of admission.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Benefit 6: Monthly Q&A */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.5
        }} className="relative" data-id="element-4736">
            <div className="absolute inset-0 bg-gradient-radial from-rose-600/10 via-transparent to-transparent blur-2xl" data-id="element-4737" />

            <div className="relative bg-gradient-to-br from-rose-950/40 to-black border-2 border-rose-500/50 p-4 sm:p-6 md:p-10 rounded-lg" data-id="element-4738">
              <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6" data-id="element-4739">
                <div className="flex-shrink-0 w-12 h-12 bg-rose-500 rounded-full flex items-center justify-center" data-id="element-4740">
                  <MessageCircle className="w-6 h-6 text-black" data-id="element-4741" />
                </div>
                <div className="flex-1" data-id="element-4742">
                  <div className="flex items-center gap-2 mb-3" data-id="element-4743">
                    <span className="text-green-400 text-2xl" data-id="element-4744">✓</span>
                    <h3 className="text-2xl md:text-3xl font-bold text-rose-400" data-id="element-4745">
                      MONTHLY Q&A SESSIONS WHERE YOU CAN GET YOUR OWN QUESTIONS
                      ANSWERED
                    </h3>
                  </div>
                </div>
              </div>

              <div className="space-y-4 text-base sm:text-lg text-stone-300 pl-0 sm:pl-16" data-id="element-4746">
                <p className="text-xl text-rose-400 font-bold" data-id="element-4747">
                  This is where PSI PRO becomes PERSONAL.
                </p>

                <p data-id="element-4748">You're not just watching pre-recorded content.</p>

                <p className="text-stone-100 font-semibold" data-id="element-4749">
                  You're getting DIRECT ACCESS to Brenden and Ali.
                </p>

                <div className="space-y-3 pl-6 border-l-2 border-rose-500/30" data-id="element-4750">
                  <p data-id="element-4751">You can submit your questions in advance.</p>
                  <p data-id="element-4752">You can ask them live during the sessions.</p>
                  <p className="text-stone-100 font-semibold" data-id="element-4753">
                    You can get answers about YOUR specific situation.
                  </p>
                </div>

                <div className="bg-rose-950/30 p-6 rounded-lg space-y-3" data-id="element-4754">
                  <p className="text-stone-100 font-bold" data-id="element-4755">YOUR protocols.</p>
                  <p className="text-stone-100 font-bold" data-id="element-4756">YOUR results.</p>
                  <p className="text-stone-100 font-bold" data-id="element-4757">YOUR challenges.</p>
                </div>

                <p className="text-xl text-rose-400 font-bold" data-id="element-4758">
                  This is similar to the kind of access that private clients pay
                  $1,500/hour for.
                </p>

                <p className="text-stone-100 font-semibold" data-id="element-4759">
                  You're getting it as part of your membership.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* CTA Button */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="mt-16 text-center" data-id="element-4760">
          <button onClick={() => {
          const pricingSection = document.querySelector('[data-pricing-section]');
          if (pricingSection) {
            pricingSection.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }} className="inline-block px-8 sm:px-12 py-4 sm:py-5 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-black font-bold text-lg sm:text-xl rounded-lg shadow-[0_0_30px_rgba(217,119,6,0.4)] hover:shadow-[0_0_50px_rgba(217,119,6,0.6)] hover:scale-105 transition-all duration-300 min-h-[44px]" data-id="element-4761">
            Join the Private Research Order Now
          </button>
        </motion.div>
      </motion.div>
    </section>;
}